import { logger } from './logger';
import ENV from './env';

const isLocalDev = typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const getBaseUrl = () => isLocalDev ? '/bio-proxy' : ENV.VITE_AWS_API_GATEWAY_URL;

export const decodeToken = (token: string) => {
  try {
    const parts = token.split('.');
    const header = JSON.parse(atob(parts[0]));
    const payload = JSON.parse(atob(parts[1]));
    return { header, payload };
  } catch {
    return null;
  }
}

import biometricAuthService from '@/services/biometricAuthService';

async function getAuthHeaders(contentType: string | null = 'application/json'): Promise<HeadersInit> {
  const headers: Record<string, string> = {};
  if (contentType) {
    headers['Content-Type'] = contentType;
  }
  try {
    // RDS endpoints now require a backend-signed JWT token.
    // getBiometricToken handles checking for local token, verifying its validity,
    // and performing admin login fallback if necessary.
    const tokenToUse = await biometricAuthService.getBiometricToken();

    if (tokenToUse) {
      headers['Authorization'] = `Bearer ${tokenToUse}`;
      const decoded = decodeToken(tokenToUse);

      logger.debug(`🔑 Auth: Using token from biometricAuthService`, {
        iss: decoded?.payload.iss,
        aud: decoded?.payload.aud,
        sub: decoded?.payload.sub || decoded?.payload.uuid
      });
    }

  } catch (error) {
    console.warn("Auth header generation failed:", error);
  }
  return headers;
}

function buildQueryString(params?: Record<string, string | number | undefined>): string {
  if (!params) return '';
  const search = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') {
      search.set(k, String(v));
    }
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

async function handleResponseError(response: Response, path: string, method: string): Promise<never> {
  const responseText = await response.text();
  
  if (response.status === 401) {
    console.warn("🔒 Received 401 Unauthorized from API, clearing local JWT tokens");
    localStorage.removeItem('jwt_token');
    localStorage.removeItem('jwt_token_expiry');
  }

  let errBody: any = {};
  try {
    errBody = JSON.parse(responseText);
  } catch {
    const trimmed = responseText.trim();
    if (trimmed.toLowerCase().startsWith('<!doctype html') || trimmed.includes('<html') || trimmed.includes('<body')) {
      errBody = { message: `Server error (${response.status}). Please try again later.` };
    } else {
      errBody = { message: responseText };
    }
  }

  logger.error(`❌ API Error [${method} ${path}]:`, {
    status: response.status,
    error: errBody.error || errBody.message,
  });

  throw new Error(errBody.error || errBody.message || `Request failed: ${response.status}`);
}

export async function apiGet<T = any>(
  path: string,
  params?: Record<string, string | number | undefined>
): Promise<T> {
  const baseUrl = getBaseUrl().replace(/\/$/, '');
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  const url = `${baseUrl}${cleanPath}${buildQueryString(params)}`;

  const headers = await getAuthHeaders();
  logger.debug(`🌐 API GET Request: ${url}`);

  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  logger.debug(`📡 API Response Status: ${response.status} for ${cleanPath}`);

  if (!response.ok) {
    await handleResponseError(response, path, 'GET');
  }
  return response.json();
}

export async function apiPost<T = any>(path: string, body?: unknown): Promise<T> {
  const baseUrl = getBaseUrl().replace(/\/$/, '');
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  logger.debug(`🌐 API POST Request: ${baseUrl}${cleanPath}`);
  const response = await fetch(`${baseUrl}${cleanPath}`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!response.ok) {
    await handleResponseError(response, path, 'POST');
  }
  return response.json();
}

export async function apiPut<T = any>(path: string, body?: unknown): Promise<T> {
  const baseUrl = getBaseUrl().replace(/\/$/, '');
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  const response = await fetch(`${baseUrl}${cleanPath}`, {
    method: 'PUT',
    headers: await getAuthHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!response.ok) {
    await handleResponseError(response, path, 'PUT');
  }
  return response.json();
}

/**
 * Perform a multipart POST request (e.g. for file uploads).
 * The browser will automatically set the Content-Type with the correct boundary.
 */
export async function apiPostMultipart<T = any>(path: string, formData: FormData): Promise<T> {
  const baseUrl = getBaseUrl().replace(/\/$/, '');
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  const url = `${baseUrl}${cleanPath}`;

  const headers = await getAuthHeaders(null);

  logger.debug(`🌐 API POST Multipart Request: ${url}`);

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    await handleResponseError(response, path, 'POST Multipart');
  }

  return response.json();
}

export async function apiDelete<T = any>(path: string, body?: unknown): Promise<T> {
  const baseUrl = getBaseUrl().replace(/\/$/, '');
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  
  const headers = await getAuthHeaders();
  
  const response = await fetch(`${baseUrl}${cleanPath}`, {
    method: 'DELETE',
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    await handleResponseError(response, path, 'DELETE');
  }
  return response.json();
}
